import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowLeft,
  Send,
  Bot,
  User,
  Loader2,
  Sparkles,
  MessageSquare
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgentChat = ({ agent, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const latestMessageRef = useRef(null);

  useEffect(() => {
    // Welcome message with starter prompts
    setMessages([{
      role: "assistant",
      content: `Hello! I'm the **${agent.name}** agent.\n\n${agent.retailExplanation}\n\nHow can I help you today?`,
      timestamp: new Date().toISOString(),
      model: "system",
      followup_questions: agent.suggestedPrompts
    }]);
  }, [agent]);

  useEffect(() => {
    if (scrollRef.current) {
      const scrollElement = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [messages]);

  const formatResponse = (text) => {
    if (!text) return text;
    return text
      .replace(/(\d+\.)\s+/g, '\n\n$1 ')
      .replace(/\s+•\s+/g, '\n\n• ')
      .replace(/([.!?])\s+(?!•|\d)([A-Z])/g, '$1\n\n$2')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  const renderFormattedText = (text) => {
    if (!text) return text;
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={idx} className="font-semibold text-white">{part.slice(2, -2)}</strong>;
      }
      return <span key={idx}>{part}</span>;
    });
  };

  const handleSend = async (messageText = null) => {
    const textToSend = messageText || input;
    if (!textToSend.trim() || loading) return;

    const userMessage = {
      role: "user",
      content: textToSend,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    // Scroll to bottom immediately
    setTimeout(() => {
      if (scrollRef.current) {
        const scrollElement = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
        if (scrollElement) scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }, 100);

    let messageAdded = false;
    const msgId = Date.now();

    try {
      const res = await fetch(`${API}/cerebro/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          message: textToSend,
          session_id: sessionId,
          model: "auto",
          mode: "executive",
          system_prompt: agent.systemPrompt
        })
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let fullText = "";
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.trim().startsWith('data: ')) {
            try {
              const data = JSON.parse(line.trim().slice(6));

              if (data.type === 'start' && !sessionId) {
                setSessionId(data.session_id);
              } else if (data.type === 'chunk') {
                fullText = data.full_text;

                if (!messageAdded) {
                  setLoading(false);
                  setMessages(prev => [...prev, {
                    role: "assistant",
                    content: formatResponse(fullText),
                    timestamp: new Date().toISOString(),
                    model: "agent",
                    messageId: msgId
                  }]);
                  messageAdded = true;
                } else {
                  setMessages(prev => prev.map(msg =>
                    msg.messageId === msgId
                      ? { ...msg, content: formatResponse(fullText) }
                      : msg
                  ));
                }
              } else if (data.type === 'done') {
                setMessages(prev => prev.map(msg =>
                  msg.messageId === msgId
                    ? {
                        ...msg,
                        content: formatResponse(fullText),
                        followup_questions: data.followup_questions || []
                      }
                    : msg
                ));
              }
            } catch (e) {
              console.log('Parse error:', e);
            }
          }
        }
      }
    } catch (e) {
      console.error("Agent chat error:", e);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "I apologize, but I encountered an error. Please try again.",
        timestamp: new Date().toISOString(),
        model: "error"
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={onBack}
            className="border-white/10 hover:bg-white/5"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <span className="text-3xl">{agent.icon}</span>
              <div>
                <h1 className="text-xl font-bold text-white">{agent.name}</h1>
                <p className="text-sm text-gray-400">{agent.category} Agent</p>
              </div>
            </div>
          </div>
        </div>
        <Badge className="bg-cyan-500/10 text-cyan-400 border-cyan-500/20">
          {agent.status === "production" ? "Production" : "Experimental"} {agent.version}
        </Badge>
      </div>

      {/* Chat Container */}
      <Card className="flex-1 bg-[#0A0A0A] border-white/10 flex flex-col overflow-hidden">
        <ScrollArea className="flex-1 p-6" ref={scrollRef}>
          <div className="space-y-6 max-w-4xl mx-auto">
            {messages.map((message, idx) => (
              <div
                key={idx}
                ref={idx === messages.length - 1 && message.role === 'assistant' ? latestMessageRef : null}
                className={`flex gap-4 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  message.role === 'user' ? 'bg-cyan-500/20' : 'bg-purple-500/10'
                }`}>
                  {message.role === 'user' ? (
                    <User className="w-5 h-5 text-cyan-400" />
                  ) : (
                    <Bot className="w-5 h-5 text-purple-400" />
                  )}
                </div>

                <div className={`flex-1 max-w-[85%] ${message.role === 'user' ? 'text-right' : ''}`}>
                  <div className={`inline-block text-left ${
                    message.role === 'user' ? 'bg-cyan-500/10 border border-cyan-500/20' : 'glass'
                  } rounded-2xl px-5 py-4`}>
                    <p className={`text-[15px] leading-relaxed whitespace-pre-wrap ${
                      message.role === 'user' ? 'text-white' : 'text-gray-200'
                    }`}>
                      {renderFormattedText(message.content)}
                    </p>

                    {/* Follow-up questions */}
                    {message.role === 'assistant' && message.followup_questions && message.followup_questions.length > 0 && (
                      <div className="mt-6 pt-4 border-t border-cyan-500/10">
                        <p className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-cyan-400" />
                          Suggested prompts
                        </p>
                        <div className="flex flex-col gap-3">
                          {message.followup_questions.map((question, qIdx) => (
                            <button
                              key={qIdx}
                              onClick={() => {
                                handleSend(question);
                                // Scroll to bottom
                                setTimeout(() => {
                                  if (scrollRef.current) {
                                    const scrollElement = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
                                    if (scrollElement) scrollElement.scrollTop = scrollElement.scrollHeight;
                                  }
                                }, 100);
                              }}
                              className="text-left text-base text-white bg-white/5 hover:bg-cyan-500/10 border border-white/10 hover:border-cyan-500/30 rounded-xl px-4 py-3.5 transition-all group cursor-pointer"
                            >
                              <span className="flex items-center gap-3">
                                <MessageSquare className="w-4 h-4 text-cyan-400 opacity-70 group-hover:opacity-100 flex-shrink-0" />
                                <span className="leading-relaxed">{question}</span>
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <p className={`text-[10px] text-gray-600 mt-1.5 ${message.role === 'user' ? 'text-right' : ''}`}>
                    {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-purple-400" />
                </div>
                <div className="glass rounded-2xl px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="text-gray-400 text-sm">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input Area */}
        <div className="border-t border-white/10 p-4">
          <div className="flex gap-3 max-w-4xl mx-auto">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={`Ask ${agent.name}...`}
              className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 resize-none"
              rows={2}
            />
            <Button
              onClick={() => handleSend()}
              disabled={!input.trim() || loading}
              className="bg-cyan-500 hover:bg-cyan-600 text-black h-auto px-6"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AgentChat;
